import { Link } from "react-router-dom";
import { Card } from "../components/ui/Card";
import { IconRecover, IconTrace, IconKey, IconCompass, IconScale } from "../components/ui/icons";

const TRUST_POINTS = ["Non-custodial", "Public blockchain data only", "No account required"];

const TRUST_DETAILS = [
  {
    icon: IconKey,
    title: "Keys never transmitted",
    body: "Recovery runs entirely on your device. Candidate seed phrases and derived keys are never sent anywhere, including to us — there's no server in that path to trust.",
  },
  {
    icon: IconCompass,
    title: "Public data only",
    body: "Tracing reads the same public ledger anyone can query. No private keys are requested or accepted for it, and none are needed — an address or a transaction hash is enough.",
  },
  {
    icon: IconScale,
    title: "Honest about limits",
    body: "Recovery works for incomplete information — a missing word, a wrong word, an unknown path — not a fully lost phrase. Tracing shows where funds moved; it can't force their return.",
  },
];

const CHAINS = [
  { symbol: "BTC", name: "Bitcoin", recover: true, trace: true },
  { symbol: "ETH", name: "Ethereum", recover: false, trace: true },
  { symbol: "SOL", name: "Solana", recover: false, trace: true },
  { symbol: "TRX", name: "Tron", recover: false, trace: true },
];

export default function Home() {
  return (
    <div>
      <section className="relative overflow-hidden border-b border-ink-700">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(139,150,165,0.18) 1px, transparent 0)",
            backgroundSize: "28px 28px",
            maskImage: "radial-gradient(ellipse 60% 60% at 30% 20%, black, transparent)",
          }}
        />
        <div
          className="pointer-events-none absolute -top-40 left-1/4 w-[560px] h-[560px] rounded-full opacity-25 blur-3xl"
          style={{ background: "radial-gradient(circle, #4A90C2 0%, transparent 70%)" }}
        />

        <div className="relative max-w-6xl mx-auto px-6 py-20 md:py-24 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-8 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-ledger-400 bg-ledger-500/10 border border-ledger-500/30 rounded-full px-3 py-1">
              Wallet recovery &amp; investigation
            </span>

            <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl leading-[1.08] text-balance">
              Recover what you still have.
              <br />
              Trace what you lost.
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted max-w-xl">
              Offline wallet recovery and blockchain transaction investigation, designed so
              your private keys never leave your device.
            </p>

            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
              {TRUST_POINTS.map((point) => (
                <span key={point} className="flex items-center gap-2 text-sm text-muted">
                  <span className="w-1.5 h-1.5 rounded-full bg-signal-400" />
                  {point}
                </span>
              ))}
            </div>
          </div>

          <div className="relative rounded-xl overflow-hidden border border-ink-700 shadow-lg shadow-black/30 aspect-[4/3] lg:aspect-square">
            <img
              src="/images/hero-trace-estuary.jpg"
              alt="A satellite view of a branching river delta — the same fan-out pattern a traced transaction's funds leave across a blockchain"
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
            />
            <div
              className="absolute inset-0"
              style={{ background: "linear-gradient(160deg, rgba(13,17,23,0.15) 0%, rgba(13,17,23,0.75) 100%)" }}
            />
            <div className="absolute inset-0 border border-white/5 rounded-xl" />
            <p className="absolute bottom-3 left-4 right-4 text-xs text-paper/80 font-mono">
              Funds branch like a delta. Tracing follows every fork.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          <Card as={Link} to="/recover" padding="lg" interactive className="group block overflow-hidden !p-0">
            <div className="aspect-[16/9] overflow-hidden">
              <img
                src="/images/feature-recover.jpg"
                alt="Macro photo of vintage typewriter type-bars, close in tone to picking out the one wrong character among many"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="p-6">
              <span className="flex items-center justify-center w-11 h-11 rounded-md bg-ledger-500/15 text-ledger-400">
                <IconRecover className="w-5 h-5" />
              </span>
              <h2 className="mt-4 font-display text-xl">Recover a wallet</h2>
              <p className="mt-2 text-sm text-muted">
                For legitimate owners with incomplete recovery information — a missing
                word, a wrong word, an unknown derivation path. Runs entirely on your
                device.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-ledger-400">
                Start recovery
                <span className="transition-transform group-hover:translate-x-0.5">→</span>
              </span>
            </div>
          </Card>
          <Card as={Link} to="/trace" padding="lg" interactive className="group block overflow-hidden !p-0">
            <div className="relative aspect-[16/9] overflow-hidden">
              <img
                src="/images/feature-trace.jpg"
                alt="Aerial view of a braided river fanning into many channels across farmland, standing in for how funds fan out across a traced blockchain graph"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              <span className="absolute bottom-1.5 right-2 text-[10px] text-paper/50">
                Photo: Andrew Cooper, CC BY 3.0
              </span>
            </div>
            <div className="p-6">
              <span className="flex items-center justify-center w-11 h-11 rounded-md bg-ledger-500/15 text-ledger-400">
                <IconTrace className="w-5 h-5" />
              </span>
              <h2 className="mt-4 font-display text-xl">Trace a transaction</h2>
              <p className="mt-2 text-sm text-muted">
                Investigate where funds went using only public blockchain data — an
                address or a transaction hash. No private keys required or accepted.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-ledger-400">
                Start tracing
                <span className="transition-transform group-hover:translate-x-0.5">→</span>
              </span>
            </div>
          </Card>
        </div>

        <p className="mt-10 text-sm text-muted">
          Neither module can guarantee recovery of stolen funds. See{" "}
          <Link to="/demo" className="text-ledger-400 underline">
            the demo
          </Link>{" "}
          for a safe, synthetic-data walkthrough of how tracing works.
        </p>
      </div>

      <div className="border-t border-ink-700">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="font-display text-2xl md:text-3xl">Built so there's nothing to trust</h2>
          <p className="mt-3 text-muted max-w-2xl">
            Every design choice here is meant to remove a reason you'd have to take our word for it.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TRUST_DETAILS.map(({ icon: Icon, title, body }) => (
              <div key={title}>
                <span className="flex items-center justify-center w-10 h-10 rounded-md bg-ledger-500/15 text-ledger-400">
                  <Icon className="w-5 h-5" />
                </span>
                <h3 className="mt-4 font-display text-lg">{title}</h3>
                <p className="mt-2 text-sm text-muted">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-ink-700">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <h2 className="font-display text-2xl md:text-3xl">Chain support</h2>
          <p className="mt-3 text-muted max-w-2xl">
            Recovery is Bitcoin-only, on purpose — it's the only chain where derivation is
            fully implemented and tested here. Tracing reads public chain data across four
            networks.
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {CHAINS.map((chain) => (
              <Card key={chain.symbol} padding="md">
                <div className="font-mono text-sm text-ledger-400">{chain.symbol}</div>
                <div className="mt-1 font-display text-base">{chain.name}</div>
                <div className="mt-3 flex flex-col gap-1 text-xs text-muted">
                  <span className={chain.recover ? "text-paper" : ""}>
                    {chain.recover ? "✓" : "—"} Recover
                  </span>
                  <span className={chain.trace ? "text-paper" : ""}>
                    {chain.trace ? "✓" : "—"} Trace
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
